import { Button, Chip, Grid, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useContext, useEffect, useState } from "react";
import { Global_CTX } from "../../contexts/global-ctx";
import { User_Service } from "../../services/user/user-service";
import { PERMISSIONS } from "../../utils/constants";
import SyncSingleUserAttendance from "../attendance/SyncSingleUserAttendance";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { Search } from "../filters/Search";
import MultiPermissionAuthorize from "../MultiPermissionAuthorize";
import New_Item from "../new-item";
import { RUDTable } from "../Table/RUDTable";
import UserContext, { User_Type } from "../UserContext";
import Create_User_Form from "./create-user-form";
import SyncPortalUser from "./SyncPortalUser";

const initial_filter = {
    org_code: "",
    org_api_key: "",
    search: "",
    branch: "",
    apply: 0,
    verfied: "",
};

export default function Users_Page() {
    const [filter, set_filter] = useState(initial_filter);
    const [refetchCount, setRefetchCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState<any>();
    const { refetch_counter } = useContext(Global_CTX);

    const { user } = useContext(UserContext);

    useEffect(() => {
        if (user?.user_type === User_Type.Admin) {
            set_filter((f) => ({
                ...f,
                org_code: user.organization.code,
                org_api_key: user.organization.apiKey[0].key,
            }));
        }
    }, [user?.user_type]);

    return (
        <Box>
            <Typography
                pt={2}
                px={2}
                color={(theme) => theme.palette.primary.main}
                variant="h5"
            >
                Users
            </Typography>

            <MultiPermissionAuthorize ops={[PERMISSIONS.CREATE_USER]}>
                <New_Item title="Create User">
                    <Create_User_Form />
                </New_Item>
            </MultiPermissionAuthorize>

            <Grid container p={2} spacing={2}>
                {user?.user_type === User_Type.Superadmin && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            label="Organization"
                            labelKey="name"
                            api="/organization"
                            setOutput={(org) => {
                                set_filter((f) => ({
                                    ...f,
                                    org_code: org?.code || "",
                                }));
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        key={`branch-input`}
                        label="Branch"
                        labelKey="name"
                        setOutput={(branch) => {
                            set_filter((curr) => ({
                                ...curr,
                                branch: branch?.id ?? "",
                            }));
                        }}
                        api={
                            user?.user_type === User_Type.Admin
                                ? "/organization/branch"
                                : "/organization/branch/superadmin"
                        }
                        apiParams={{
                            organization:
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : filter.org_code,
                        }}
                        request_headers={{
                            "x-org":
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : filter.org_code,
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        label="Verification Status"
                        labelKey="name"
                        setOutput={(v) => {
                            set_filter((f) => ({
                                ...f,
                                verfied: v?.id || "",
                            }));
                        }}
                        defaultOptions={[
                            { id: "1", name: "Verified" },
                            { id: 0, name: "Pending" },
                        ]}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Search filter={filter} setFilter={set_filter} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Button
                        color="primary"
                        fullWidth
                        variant="contained"
                        onClick={() =>
                            set_filter({
                                ...filter,
                                apply: (filter.apply ?? 0) + 1,
                            })
                        }
                        style={{ height: "100%" }}
                        size="small"
                    >
                        {" "}
                        Search
                    </Button>
                </Grid>
            </Grid>

            <RUDTable
                storageKey="users-page"
                updatingAgents={[
                    filter.org_code,
                    filter.apply,
                    refetchCount,
                    refetch_counter,
                ]}
                getter={(page, limit) =>
                    User_Service.get_instance().get_users(
                        filter.org_code,
                        filter.org_api_key,

                        {
                            page,
                            limit,
                            search: filter.search ? filter.search : undefined,
                            branch: filter.branch ? filter.branch : undefined,
                            verfied: filter.verfied
                                ? filter.verfied
                                : undefined,
                        }
                    )
                }
                deletor={(id, row) => {
                    return User_Service.get_instance().delete_user(
                        row?.branch?.id,
                        row.id,
                        row.pin,
                        filter.org_code,
                        filter.org_api_key
                    );
                }}
                editor={(...args) => {
                    const [id, changes, row] = args;
                    return User_Service.get_instance().update_user(
                        row.branch.id,
                        row.pin,
                        filter.org_code,
                        changes,
                        filter.org_api_key
                    );
                }}
                rowsPreprocessor={(row) => ({
                    ...row,
                    id: row.id,
                    _verified: (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={row.verfied === 1 ? "Verified" : "Pending"}
                            color={row.verfied === 1 ? "success" : "warning"}
                            // icon={row.verfied === 1 ? <Check /> : <Info />}
                        />
                    ),
                    deletionId: row.pin,
                    default_status: {
                        id: row.verfied,
                        name: row.verfied === 1 ? "Verified" : "Pending",
                    },
                })}
                ops={{
                    read: PERMISSIONS.READ_USER,
                    edit: PERMISSIONS.UPDATE_USER,
                    delete: PERMISSIONS.ARCHIVE_USER,
                }}
                readables={{
                    id: "ID",
                    name: "Name",
                    password: "Password",
                    card: "Card",
                    pin: "PIN",
                    group: "Group",
                    privilege: "Privilege",
                    _verified: "Verification Status",
                    Sync: "Sync User",
                    "Sync Attendance": "Sync Attendance",
                }}
                editables={[
                    {
                        name: "name",
                        type: "textfield",
                        label: "Name",
                    },

                    {
                        name: "verfied",
                        type: "autocomplete",
                        label: "Verification Status",
                        isRequired: true,
                        autocomplete: {
                            labelKey: "name",
                            label: "Verification",
                            defaultOptions: [
                                { id: 1, name: "Verified" },
                                { id: 0, name: "Pending" },
                            ],
                            defaultValue: "default_status",
                        },
                    },
                ]}
                customCols={[
                    {
                        header: "Sync",
                        content: (row) => (
                            <SyncPortalUser
                                rows={[row]}
                                disabled={false}
                                onSuccess={() => {
                                    setRefetchCount((n) => n + 1);
                                }}
                            />
                        ),
                    },

                    {
                        header: "Sync Attendance",
                        content: (row) => (
                            <SyncSingleUserAttendance
                                rows={[row]}
                                disabled={false}
                                onSuccess={() => {
                                    setRefetchCount((n) => n + 1);
                                }}
                            />
                        ),
                    },
                ]}
                getSelectedRows={(rows) => {
                    setSelectedRows(rows);
                }}
                actions={[<SyncPortalUser rows={selectedRows} />]}
            />
        </Box>
    );
}
